// src/screens/TicketsScreen.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import './TicketsScreen.css';
import { callGetTickets } from '../functions';
import LoadingIndicator from '../components/LoadingIndicator';
import RowItem from '../components/RowItem';
import { CompanyContext } from '../context/CompanyContext';
import useTicketStatusParser from '../utils/ticketUtils';
import { Fab } from '@mui/material';
import { FaPlus } from 'react-icons/fa';
import SearchBar from '../components/SearchBar';
import TicketsScreenModal from '../modals/TicketsScreenModal';

const TicketsScreen = () => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [allTicketsForDateRange, setAllTicketsForDateRange] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const { selectedCompany } = useContext(CompanyContext);
  const { parseTicketStatus, getPriorityFileSrc } = useTicketStatusParser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const priorityPriority = {
    "Low": 0,
    "Medium": 1,
    "High": 2,
    "CRITICAL": 3
  }

  const statusPriority = {
    "OPEN": 0,
    "IN PROGRESS": 1,
    "CLOSED": 2,
    "ON HOLD": 3
  }

  const fetchTickets = async () => {
    const getFilterFromStorage = () => {
      const filter = localStorage.getItem('ticketFilter') || "{}";
      const { dateRange, department, statuses, property, sortDirection, sortField, priorities } = JSON.parse(filter);
      return {
        dateRange: dateRange || 30,
        department: department || null,
        statuses: statuses || ['OPEN', 'IN PROGRESS', 'CLOSED', 'ON HOLD'],
        property: property || null,
        sortDirection: sortDirection || 'desc',
        sortField: sortField || 'number',
        priorities: priorities || ['Low', 'Medium', 'High', 'CRITICAL']
      };
    };
    setLoading(true);
    try {
      const filter = getFilterFromStorage();
      console.log('filter', filter);
      const oneWeekInSeconds = filter.dateRange * 24 * 60 * 60;
      const endTime = Math.floor(Date.now() / 1000);
      const startTime = endTime - oneWeekInSeconds;
      const response = 
        dateRange === filter.dateRange ?
        allTicketsForDateRange :
        await callGetTickets(selectedCompany.id, startTime, endTime);

      setAllTicketsForDateRange(response);
      setDateRange(filter.dateRange);

      const filtered = response.filter((ticket) => {
        if (filter.property) {
          if (ticket.property?.id !== filter.property.id) {
            return false;
          }
        }
        if (filter.department) {
          if (ticket.department?.id !== filter.department.id) {
            return false;
          }
        }
        if (filter.statuses.length > 0) {
          if (!filter.statuses.includes(ticket.status)) {
            return false;
          }
        }
        if (filter.priorities.length > 0) {
          if (!filter.priorities.includes(ticket.priority)) {
            return false;
          }
        }

        return true;
      })

      console.log('Filter', filtered);

      // Sort tickets based on the selected sort field and direction
      if (filter.sortField === 'number') {
        filtered.sort((a, b) => {
          return filter.sortDirection === 'asc' ? a.ticketNumber - b.ticketNumber : b.ticketNumber - a.ticketNumber;
        });
      } else if (filter.sortField === 'date') {
        filtered.sort((a, b) => {
          return filter.sortDirection === 'asc' ? a.date - b.date : b.date - a.date;
        });
      } else if (filter.sortField === 'priority') {
        filtered.sort((a, b) => {
          return filter.sortDirection === 'asc' ? priorityPriority[a.priority] - priorityPriority[b.priority] : priorityPriority[b.priority] - priorityPriority[a.priority];
        });
      } else if (filter.sortField === 'status') {
        filtered.sort((a, b) => {
          return filter.sortDirection === 'asc' ? statusPriority[a.status] - statusPriority[b.status] : statusPriority[b.status] - statusPriority[a.status];
        });
      } else if (filter.sortField === 'property') {
        filtered.sort((a, b) => {
          if (a.property && b.property) { 
            return filter.sortDirection === 'asc' ? a.property.name.localeCompare(b.property.name) : b.property.name.localeCompare(a.property.name);
          }
          return 0; // If property is not defined, do not sort
        });
      } else if (filter.sortField === 'department') {
        filtered.sort((a, b) => {
          if (a.department && b.department) {
            return filter.sortDirection === 'asc' ? a.department.name.localeCompare(b.department.name) : b.department.name.localeCompare(a.department.name);
          }
          return 0; // If department is not defined, do not sort
        });
      } else if (filter.sortField === 'title') {
        filtered.sort((a, b) => {
          return filter.sortDirection === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
        });
      }

      setTickets(filtered);
    } catch(error) {
      console.log(error);
      alert('Error fetching tickets'); // TODO: JD translation
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCompany) return;
    fetchTickets();
  }, [selectedCompany]);

  const filteredTickets = tickets.filter(ticket =>
    ticket.title.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleTicketClick = (ticket) => {
    navigate(`/tickets/${ticket.id}`, { state: { ticket } });
  };

  const handleAddTicket = () => {
    navigate('/tickets/new');
  };

  const handleFiltersClick = () => {
    setIsModalOpen(true);
  };

  const handleFilterClose = () => {
    setIsModalOpen(false);
    fetchTickets();
  }

  const formatDate = (date) => {
      // Format date to display month, day, hour and minuts in system locale
      const options = { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
      const formattedDate = new Date(date * 1000).toLocaleString(undefined, options);
      return formattedDate;
  }

  return (
    <div className="tickets-container">
      <NavBar title={t("tickets.title")} className="navbar" rightButtonTitle={t("tickets.filters")} onRightButtonClick={handleFiltersClick} />
      <SearchBar
        placeholder={t('scheduleExecution.areaList.search')}
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
      />
      {loading ? (
        <LoadingIndicator />
      ) : (
        <div className="tickets-list">
          {filteredTickets.map(ticket => (
            <RowItem
              key={ticket.id}
              title={ticket.title}
              secondaryTitle={
                ticket.ticketNumber + 
                " • " + 
                ticket.property.name +
                " • " + 
                parseTicketStatus(ticket.status)
              }
              // details={formatDate(ticket.date)}
              // secondaryDetails={parseTicketStatus(ticket.status)}
              image={getPriorityFileSrc(ticket.priority)}
              onClick={() => handleTicketClick(ticket)}
            />
          ))}
        </div>
      )}
      <TicketsScreenModal isOpen={isModalOpen} onRequestClose={() => {handleFilterClose()}} />
      {!isModalOpen &&
        <Fab
          color="primary"
          aria-label="add"
          className="fab"
          onClick={handleAddTicket}
        >
          <FaPlus />
        </Fab>
      }
    </div>
  );
};

export default TicketsScreen;